import { memo, useState } from 'react';
import { Button, Menu, MenuItem, Box, Divider, SxProps, Typography, Stack, colors } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { logout as logoutAction } from '../store/slices/authSlice';
import { LogoutOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { SUPPORT_EMAIL } from '../constants/supportConstants';
import { selectFetchError } from '../store/selectors/translationsSelectors';

const Account = memo(() => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentUser = useAppSelector(state => state.userProfile.data);
  const translationsError = useAppSelector(selectFetchError);
  const user = useAppSelector(state => state.auth.user);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
      handleClose();
      dispatch(logoutAction());
      navigate('/login');
  };

  if (!user) {
    return null
  }

  return (
    <Box>
      <Button
        id="account-button"
        onClick={handleClick}
        color="primary"
        aria-controls={open ? "account-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        disabled={!!translationsError}
      >
        ACCOUNT
      </Button>
      <Menu
        id="account-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          list: {
            'aria-labelledby': "account-button",
          }
        }}
      >
        <Stack spacing={3} sx={styles.accountDetails}>
          <Box>
            <Typography variant="h6">{user.name}</Typography>
            <Typography variant="subtitle2" className="user-role">{currentUser?.role}</Typography>
          </Box>
          <Box>
            <Typography variant="body2">
              Need more access? To upgrade contact <span className="email">{SUPPORT_EMAIL}</span>
            </Typography>
          </Box>
        </Stack>
        <Divider />
        <MenuItem onClick={handleLogout} sx={styles.logoutItem}>
          <LogoutOutlined fontSize="small" color="primary" />
          logout
        </MenuItem>
      </Menu>
    </Box>
  )
});

const styles: Record<string, SxProps> = {
  accountDetails: {
    userSelect: 'none',
    maxWidth: '318px',
    p: 3,
    '.user-role': {
      color: 'rgba(0, 0, 0, 0.6)',
    },
    '.email': {
      color: colors.blue[700],
      cursor: 'pointer',
    },
  },
  logoutItem: {
    color: colors.blue[700],
    textTransform: 'uppercase',
    fontWeight: 500,
    '.MuiSvgIcon-root': {
      marginRight: '4.2px',
    }
  },
  menuTitle: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    minWidth: '204px',
    padding: '0 8px',
    '@media (max-width: 768px)': {
      minWidth: '180px',
    },
  },
};

export default Account;
